/** @file An edit-mesh press: becomes a vertex drag or a marquee past 3 px, else a click (select or insert). */
import { HANDLE_PX } from '@pose2d/editor-geometry/handlePx';
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import type { ToolGesture, ViewportPointer } from '../../panels/viewport/viewportTypes';
import type { PendingCommand } from '../../stores/types';
import { selectModeOf } from '../select/selectModeOf';
import { editMeshStore } from './editMeshStore';
import type { MeshScene } from './meshScene';
import { pendingOf } from './pendingOf';
import { selectVertices } from './selectVertices';
import { insertVertexPlan } from './topology/insertVertexPlan';
import { topologyCalls } from './topology/topologyCalls';
import { vertexDragGesture } from './vertexDragGesture';
import { vertexMarquee } from './vertexMarquee';

/** Screen pixels a press travels before it becomes a drag or a marquee. */
const DRAG_PX = 3;

/** Create mode: one vertex at the pointer (edge split, triangle split or hull extension), selected; Setup only. */
function insertAt(s: MeshScene, p: ViewportPointer): PendingCommand[] {
  const { ctx } = s;
  if (ctx.tool.mode === 'animate') {
    ctx.notify({ kind: 'info', key: 'tools.editMesh.setupOnly' });
    return [];
  }
  const [x, y] = viewCamera.screenToWorld(ctx.camera, p.sx, p.sy, [0, 0]);
  const plan = insertVertexPlan(s.target.mesh, s.points, x, y, HANDLE_PX.bone * viewCamera.worldPerPixel(ctx.camera));
  if (plan === null) return [];
  selectVertices(s.target.attachment, [plan.sources.findIndex((src) => src.kind === 'blend')], 'replace');
  return pendingOf(topologyCalls(ctx.doc, s.target, plan));
}

/** A press that never moved: create mode inserts on empty space; modify selects the vertex or clears on nothing. */
function click(s: MeshScene, press: ViewportPointer, hit: number, p: ViewportPointer): PendingCommand[] {
  if (editMeshStore.getState().mode === 'create') return hit < 0 ? insertAt(s, p) : [];
  const mode = selectModeOf(press);
  if (hit >= 0) selectVertices(s.target.attachment, [hit], mode);
  else if (mode === 'replace') selectVertices(s.target.attachment, [], 'replace');
  return [];
}

/** The gesture of a modify/create press on `hit` (−1 = no vertex under the pointer). */
export function editMeshPress(s: MeshScene, press: ViewportPointer, hit: number): ToolGesture {
  let inner: ToolGesture | null = null;
  let refused = false;
  return {
    move(p) {
      if (inner !== null) {
        inner.move(p);
        return;
      }
      if (refused || editMeshStore.getState().mode !== 'modify' || Math.hypot(p.sx - press.sx, p.sy - press.sy) < DRAG_PX) return;
      inner = hit >= 0 ? vertexDragGesture(s, hit, press) : vertexMarquee(s, press);
      refused = inner === null;
      inner?.move(p);
    },
    release(p) {
      if (inner !== null) return inner.release(p);
      return refused ? [] : click(s, press, hit, p);
    },
    cancel() {
      inner?.cancel();
    },
  };
}
